/**
 * eventIcons.js — Shared SVG icon renderers for BPMN 2.0 event types.
 *
 * Each renderer returns an array of h() elements to be spread inside the
 * event shape's <g> group.  The `filled` flag controls catch (outline) vs
 * throw (filled) rendering.  `scale` adjusts sizes for different radii
 * (1.0 → r=18, 0.83 → r=15 BoundaryEvent).
 */

const ICON_RENDERERS = {
  /**
   * Message — envelope: rectangle + V-shaped flap
   */
  Message(h, x, y, filled, stroke, s) {
    const w = 10 * s, ht = 7 * s;
    const left = x - w / 2, top = y - ht / 2;
    return [
      h('rect', {
        x: left, y: top, width: w, height: ht, rx: 0.5,
        fill: filled ? stroke : 'none', stroke,
        strokeWidth: 1.2 * s,
      }),
      h('polyline', {
        points: `${left},${top} ${x},${y + 0.5 * s} ${left + w},${top}`,
        fill: 'none',
        stroke: filled ? '#fff' : stroke,
        strokeWidth: 1.2 * s,
        strokeLinejoin: 'round',
      }),
    ];
  },

  /**
   * Timer — circle + two clock hands (12 o'clock and ~2 o'clock)
   */
  Timer(h, x, y, _filled, stroke, s) {
    const r = 6 * s;
    const elements = [
      h('circle', {
        cx: x, cy: y, r, fill: 'none', stroke, strokeWidth: 1 * s,
      }),
    ];
    // 12 tick marks around the clock face
    for (let i = 0; i < 12; i++) {
      const angle = (i * Math.PI) / 6;
      const inner = r - 1.5 * s;
      elements.push(h('line', {
        x1: x + inner * Math.sin(angle),
        y1: y - inner * Math.cos(angle),
        x2: x + r * Math.sin(angle),
        y2: y - r * Math.cos(angle),
        stroke, strokeWidth: 0.5 * s,
      }));
    }
    // Hour hand (12 o'clock)
    elements.push(h('line', {
      x1: x, y1: y, x2: x, y2: y - r + 2 * s,
      stroke, strokeWidth: 1 * s,
    }));
    // Minute hand (~2 o'clock)
    elements.push(h('line', {
      x1: x, y1: y, x2: x + 3 * s, y2: y - 2 * s,
      stroke, strokeWidth: 1 * s,
    }));
    return elements;
  },

  /**
   * Signal — upward-pointing triangle
   */
  Signal(h, x, y, filled, stroke, s) {
    const sz = 6 * s;
    const points = `${x},${y - sz} ${x + sz * 0.87},${y + sz * 0.5} ${x - sz * 0.87},${y + sz * 0.5}`;
    return [
      h('polygon', {
        points, fill: filled ? stroke : 'none', stroke, strokeWidth: 1.2 * s,
        strokeLinejoin: 'round',
      }),
    ];
  },

  /**
   * Error — zigzag / lightning bolt polyline
   */
  Error(h, x, y, filled, stroke, s) {
    const points = [
      [x - 4.5 * s, y + 5 * s],
      [x - 1 * s, y - 1 * s],
      [x + 1.5 * s, y + 2 * s],
      [x + 4.5 * s, y - 5 * s],
    ];
    const pts = points.map(p => p.join(',')).join(' ');
    if (filled) {
      // Closed polygon for filled version
      const allPts = [
        ...points,
        [x + 1.5 * s, y + 2 * s],
        [x - 1 * s, y - 1 * s],
      ].map(p => p.join(',')).join(' ');
      return [
        h('polygon', {
          points: allPts, fill: stroke, stroke, strokeWidth: 1 * s,
          strokeLinejoin: 'round',
        }),
      ];
    }
    return [
      h('polyline', {
        points: pts, fill: 'none', stroke, strokeWidth: 1.5 * s,
        strokeLinejoin: 'round', strokeLinecap: 'round',
      }),
    ];
  },

  /**
   * Escalation — narrow upward chevron / arrow
   */
  Escalation(h, x, y, filled, stroke, s) {
    const points = `${x},${y - 6 * s} ${x + 4 * s},${y + 5 * s} ${x},${y + 1.5 * s} ${x - 4 * s},${y + 5 * s}`;
    return [
      h('polygon', {
        points, fill: filled ? stroke : 'none', stroke, strokeWidth: 1.2 * s,
        strokeLinejoin: 'round',
      }),
    ];
  },

  /**
   * Conditional — small page/document with horizontal lines
   */
  Conditional(h, x, y, _filled, stroke, s) {
    const w = 8 * s, ht = 10 * s;
    const left = x - w / 2, top = y - ht / 2;
    return [
      h('rect', {
        x: left, y: top, width: w, height: ht,
        fill: 'none', stroke, strokeWidth: 1.2 * s,
      }),
      h('line', { x1: left + 1.5 * s, y1: top + 2.5 * s, x2: left + w - 1.5 * s, y2: top + 2.5 * s, stroke, strokeWidth: 0.8 * s }),
      h('line', { x1: left + 1.5 * s, y1: top + 5 * s, x2: left + w - 1.5 * s, y2: top + 5 * s, stroke, strokeWidth: 0.8 * s }),
      h('line', { x1: left + 1.5 * s, y1: top + 7.5 * s, x2: left + w - 1.5 * s, y2: top + 7.5 * s, stroke, strokeWidth: 0.8 * s }),
    ];
  },

  /**
   * Compensation — double left-pointing triangles (rewind)
   */
  Compensation(h, x, y, filled, stroke, s) {
    const sz = 5 * s;
    const gap = 0.5 * s;
    // Left triangle
    const t1 = `${x - gap},${y} ${x - gap - sz},${y - sz} ${x - gap - sz},${y + sz}`;
    // Right triangle
    const t2 = `${x + gap + sz},${y} ${x + gap},${y - sz} ${x + gap},${y + sz}`;
    const f = filled ? stroke : 'none';
    return [
      h('polygon', { points: t1, fill: f, stroke, strokeWidth: 1 * s, strokeLinejoin: 'round' }),
      h('polygon', { points: t2, fill: f, stroke, strokeWidth: 1 * s, strokeLinejoin: 'round' }),
    ];
  },

  /**
   * Cancel — thick X
   */
  Cancel(h, x, y, filled, stroke, s) {
    const d = 5.5 * s;
    const sw = filled ? 2.5 * s : 2 * s;
    return [
      h('line', { x1: x - d, y1: y - d, x2: x + d, y2: y + d, stroke, strokeWidth: sw, strokeLinecap: 'round' }),
      h('line', { x1: x + d, y1: y - d, x2: x - d, y2: y + d, stroke, strokeWidth: sw, strokeLinecap: 'round' }),
    ];
  },

  /**
   * Link — right-pointing arrow / chevron
   */
  Link(h, x, y, filled, stroke, s) {
    const points = `${x - 5 * s},${y - 4 * s} ${x + 2 * s},${y - 4 * s} ${x + 6 * s},${y} ${x + 2 * s},${y + 4 * s} ${x - 5 * s},${y + 4 * s} ${x - 5 * s},${y - 4 * s}`;
    return [
      h('polygon', {
        points, fill: filled ? stroke : 'none', stroke, strokeWidth: 1.2 * s,
        strokeLinejoin: 'round',
      }),
    ];
  },

  /**
   * Terminate — filled circle (always filled)
   */
  Terminate(h, x, y, _filled, stroke, s) {
    return [
      h('circle', {
        cx: x, cy: y, r: 7 * s, fill: stroke, stroke: 'none',
      }),
    ];
  },

  /**
   * Multiple — pentagon
   */
  Multiple(h, x, y, filled, stroke, s) {
    const r = 6.5 * s;
    const pts = [];
    for (let i = 0; i < 5; i++) {
      const angle = (i * 2 * Math.PI) / 5 - Math.PI / 2;
      pts.push(`${x + r * Math.cos(angle)},${y + r * Math.sin(angle)}`);
    }
    return [
      h('polygon', {
        points: pts.join(' '), fill: filled ? stroke : 'none', stroke,
        strokeWidth: 1.2 * s, strokeLinejoin: 'round',
      }),
    ];
  },

  /**
   * ParallelMultiple — plus / cross
   */
  ParallelMultiple(h, x, y, _filled, stroke, s) {
    const d = 6 * s;
    const sw = 2.5 * s;
    return [
      h('line', { x1: x, y1: y - d, x2: x, y2: y + d, stroke, strokeWidth: sw, strokeLinecap: 'round' }),
      h('line', { x1: x - d, y1: y, x2: x + d, y2: y, stroke, strokeWidth: sw, strokeLinecap: 'round' }),
    ];
  },
};

/**
 * Return an array of h() SVG elements for the given event trigger icon.
 *
 * @param {Function} h       — hyperscript function from LogicFlow
 * @param {number}   x       — center X of the event circle
 * @param {number}   y       — center Y of the event circle
 * @param {string}   trigger — trigger name (e.g. 'Timer', 'Message', '')
 * @param {boolean}  filled  — true for throw events, false for catch events
 * @param {string}   strokeColor — stroke color of the event circle
 * @param {number}   [scale=1] — scale factor (1.0 for r=18, 0.83 for r=15)
 * @returns {Array}  array of h() elements (may be empty)
 */
export function getEventIconElements(h, x, y, trigger, filled, strokeColor, scale) {
  if (!trigger) return [];
  const renderer = ICON_RENDERERS[trigger];
  if (!renderer) return [];
  return renderer(h, x, y, filled, strokeColor, scale || 1);
}
